import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MEMBER_SIGN_IN_URL, PAGE_MEMBER } from 'App/routes/constants';
import { setAutoEmailSession } from 'App/services/autoEmailService';
import { useIsLoggedIn } from 'App/services/idpTokenService';
import { AutoLoginRoot } from 'Pages/autoLogin/style';
import AELoginGuard from 'Shared/components/design/email/autoLoginGuard';
import { LOGIN_TEST_ID } from 'Shared/constants/email/testId';
import { useQueryParam } from 'Shared/hooks/useQueryParam';
import { extractAutoLoginRedirectionPath } from 'Shared/services/helperService';

/**
 * Reviews the query parameters in the url and removes
 * all parameters which are used for auto login to avoid
 * any conflicts or unintended consequences.
 *
 * @returns string
 */
function extractSafeSearchParams(): string {
  const search: string = window.location.search;
  const searchParams   = new URLSearchParams(search);
  searchParams.delete('userId');
  searchParams.delete('aeT');
  searchParams.delete('autoLoginKey');
  searchParams.delete('confirmed-email');
  searchParams.delete('loadingText');
  searchParams.delete('umid');
  searchParams.delete('aeCId');
  searchParams.delete('aeId');

  const searchString = searchParams.toString();

  if (searchString) {
    return `?${searchString}`;
  }

  return '';
}

const EMAIL_CONFIRMED = '1';
const EMAIL_ALREADY_CONFIRMED = '2';
const EMAIL_CONFIRMATION_EXPIRED_CODE = '3';

/**
 * Renders the AutoLogin page
 * Example URL http://localhost:8080/al/?aeId=100&aeCId=2&userId=767&aeT=2023-03-22T23:36:28Z&autoEmailKey=foo
 * Also handles email confirmation: /confirm-email?userId=123&aeT=2023-03-22T23:36:28Z&autoLoginKey=foo&confirmed-email=1
 */
function AutoLogin(): JSX.Element {
  const userId          = useQueryParam('userId') || '';
  const aeT             = useQueryParam('aeT', true) || '';
  const autoLoginKey    = useQueryParam('autoLoginKey') || '';
  const confirmedEmail  = useQueryParam('confirmed-email') || '';
  const loadingText     = useQueryParam('loadingText') || '';
  const stillLoading    = false;
  const isLoggedIn      = useIsLoggedIn();
  const navigate        = useNavigate();
  const location        = useLocation();
  const pathToRedirect  = extractAutoLoginRedirectionPath(location.pathname);

  // check if this is an email confirmation flow
  const isEmailConfirmation = location.pathname.includes('confirm-email');

  /**
   * Determine if the user should auto login
   * For email confirmation, require userId, aeT, autoLoginKey, and confirmed-email (1, 2, or 3)
   * For auto email, require userId, aeT, and autoLoginKey
   */
  const shouldAutoLogin = useMemo(() => {
    if (isEmailConfirmation) {
      return userId && aeT && autoLoginKey && confirmedEmail && [EMAIL_CONFIRMED, EMAIL_ALREADY_CONFIRMED, EMAIL_CONFIRMATION_EXPIRED_CODE].includes(confirmedEmail);
    }
    return userId && aeT && autoLoginKey;
  }, [aeT, autoLoginKey, userId, confirmedEmail, isEmailConfirmation]);

  /**
   * Set the auto email session
   */
  useEffect(() => {
    setAutoEmailSession('', '', aeT);
  }, [aeT]);

  /**
   * Redirect to the appropriate page based on the current state
   * For email confirmation, redirect to home page after successful login
   */
  useEffect(() => {
    if (isLoggedIn && pathToRedirect) {
      const searchParams = extractSafeSearchParams();
      navigate({
        pathname: pathToRedirect,
        search: searchParams
      }, {
        replace: true,
      });
    } else if (isLoggedIn && isEmailConfirmation) {
      navigate(PAGE_MEMBER, { replace: true });
    } else if (!shouldAutoLogin) {
      navigate(MEMBER_SIGN_IN_URL);
    }
  }, [isLoggedIn, navigate, pathToRedirect, shouldAutoLogin, isEmailConfirmation]);

  if (!shouldAutoLogin) {
    return <></>;
  }

  return (
    <AutoLoginRoot data-testid={LOGIN_TEST_ID}>
      <AELoginGuard
        userId                 = {userId}
        aeT                    = {aeT}
        autoLoginKey           = {autoLoginKey}
        stillLoading           = {stillLoading}
        isAutoLogin
        loadingText            = {loadingText}
        fallbackRedirectionURL = {MEMBER_SIGN_IN_URL}
      >
        <></>
      </AELoginGuard>
    </AutoLoginRoot>
  );
}

export default AutoLogin;
